import React from 'react';
import { PrScanEngine, PrScanMode } from '../../types/application';

export interface RepositoryPrScanSettingsValue {
  pr_scan_enabled: boolean;
  pr_scan_mode: PrScanMode;
  pr_scan_engines: PrScanEngine[];
}

interface RepositoryPrScanSettingsProps {
  value: RepositoryPrScanSettingsValue;
  onChange: (nextValue: RepositoryPrScanSettingsValue) => void;
  disabled?: boolean;
  className?: string;
  surface?: 'card' | 'plain';
  compact?: boolean;
}

export const PR_SCAN_ENGINE_ORDER: PrScanEngine[] = ['secrets', 'semgrep', 'ai'];

export const DEFAULT_REPOSITORY_PR_SCAN_SETTINGS: RepositoryPrScanSettingsValue = {
  pr_scan_enabled: true,
  pr_scan_mode: 'partial',
  pr_scan_engines: [...PR_SCAN_ENGINE_ORDER],
};

export const normalizeRepositoryPrScanSettings = (
  value?: Partial<RepositoryPrScanSettingsValue>
): RepositoryPrScanSettingsValue => {
  const normalizedEngines = PR_SCAN_ENGINE_ORDER.filter((engine) =>
    (value?.pr_scan_engines ?? DEFAULT_REPOSITORY_PR_SCAN_SETTINGS.pr_scan_engines).includes(engine)
  );

  return {
    pr_scan_enabled: value?.pr_scan_enabled ?? DEFAULT_REPOSITORY_PR_SCAN_SETTINGS.pr_scan_enabled,
    pr_scan_mode: value?.pr_scan_mode ?? DEFAULT_REPOSITORY_PR_SCAN_SETTINGS.pr_scan_mode,
    pr_scan_engines: normalizedEngines,
  };
};

const ENGINE_LABELS: Record<PrScanEngine, string> = {
  secrets: 'Secrets',
  semgrep: 'Semgrep',
  ai: 'AI',
};

const MODE_LABELS: Record<PrScanMode, string> = {
  partial: 'Partial',
  full: 'Full',
};

const RepositoryPrScanSettings: React.FC<RepositoryPrScanSettingsProps> = ({
  value,
  onChange,
  disabled = false,
  className = '',
  surface = 'card',
  compact = false,
}) => {
  const isControlDisabled = disabled || !value.pr_scan_enabled;
  const hasEngineValidationError = value.pr_scan_enabled && value.pr_scan_engines.length === 0;
  const containerClassName =
    surface === 'card'
      ? `mt-3 rounded-lg border border-ui-border-primary p-3 ${className}`
      : className;
  const sectionSpacingClassName = compact ? 'mt-2' : 'mt-3';

  const updateMode = (nextMode: PrScanMode) => {
    onChange({ ...value, pr_scan_mode: nextMode });
  };

  const toggleEnabled = () => {
    onChange({ ...value, pr_scan_enabled: !value.pr_scan_enabled });
  };

  const toggleEngine = (engine: PrScanEngine) => {
    const hasEngine = value.pr_scan_engines.includes(engine);
    const nextEngines = hasEngine
      ? value.pr_scan_engines.filter((existingEngine) => existingEngine !== engine)
      : [...value.pr_scan_engines, engine];
    const orderedEngines = PR_SCAN_ENGINE_ORDER.filter((orderedEngine) =>
      nextEngines.includes(orderedEngine)
    );
    onChange({ ...value, pr_scan_engines: orderedEngines });
  };

  return (
    <div className={containerClassName}>
      <div className='flex items-center justify-between'>
        <p className='text-xs font-semibold uppercase tracking-wide text-ui-text-secondary'>
          PR/MR scans
        </p>
        <button
          type='button'
          onClick={toggleEnabled}
          disabled={disabled}
          className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium transition-colors ${
            value.pr_scan_enabled
              ? 'bg-brand-primary/15 text-brand-primary dark:text-brand-primaryDarkMode'
              : 'bg-neutral-200 text-ui-text-secondary dark:bg-ui-background-tertiary dark:text-ui-text-tertiary'
          } ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
        >
          {value.pr_scan_enabled ? 'Enabled' : 'Disabled'}
        </button>
      </div>

      <div className={sectionSpacingClassName}>
        <p className='text-xs text-ui-text-tertiary mb-1'>Mode</p>
        <div className='flex items-center gap-2'>
          {(['partial', 'full'] as PrScanMode[]).map((mode) => (
            <button
              key={mode}
              type='button'
              onClick={() => updateMode(mode)}
              disabled={isControlDisabled}
              className={`px-2.5 py-1 text-xs rounded-md border transition-colors ${
                value.pr_scan_mode === mode
                  ? 'border-brand-primary bg-brand-primary/10 text-brand-primary dark:text-brand-primaryDarkMode'
                  : 'border-ui-border-primary text-ui-text-secondary dark:border-ui-border-primary dark:text-ui-text-tertiary'
              } ${isControlDisabled ? 'opacity-60 cursor-not-allowed' : 'hover:bg-ui-background-tertiary'}`}
            >
              {MODE_LABELS[mode]}
            </button>
          ))}
        </div>
      </div>

      <div className={sectionSpacingClassName}>
        <p className='text-xs text-ui-text-tertiary mb-1'>Engines</p>
        <div className='flex flex-wrap items-center gap-3'>
          {PR_SCAN_ENGINE_ORDER.map((engine) => (
            <label
              key={engine}
              className={`inline-flex items-center gap-1.5 text-xs ${
                isControlDisabled ? 'text-ui-text-tertiary' : 'text-ui-text-secondary'
              }`}
            >
              <input
                type='checkbox'
                checked={value.pr_scan_engines.includes(engine)}
                disabled={isControlDisabled}
                onChange={() => toggleEngine(engine)}
                className='rounded border-ui-border-primary text-brand-primary focus:ring-brand-primary'
              />
              {ENGINE_LABELS[engine]}
            </label>
          ))}
        </div>
        {hasEngineValidationError && (
          <p className='mt-2 text-xs text-red-600 dark:text-red-400'>
            Select at least one scan engine when PR/MR scans are enabled.
          </p>
        )}
      </div>
    </div>
  );
};

export default RepositoryPrScanSettings;
